/**
 * Trackerity — Creative upload size limit page (OPUS-D18 / feature #86).
 *
 * Route: /creatives/size-limit
 *
 * Admin page for inspecting and editing the per-tenant creative upload
 * ceiling. Delegates all form behaviour to the `SizeLimitForm` feature
 * component.
 *
 * Styling per brand rules: light theme only, emerald->sky gradient heading,
 * Inter font, 6-8px rounded corners, zinc borders. No emojis, no competitor
 * names.
 *
 * NOTE: This route is NOT wired into `router.tsx` by the agent — the scaffold
 * owner (root) performs the wire-up. The file is exported via `Route` so the
 * router can compose it.
 */

import { createRoute } from "@tanstack/react-router";
import { rootRoute } from "./__root";
import { CreativeSizeLimitPage } from "../features/creative-size-limit/CreativeSizeLimitPage";

export function CreativeSizeLimitRoute() {
	return (
		<main className="mx-auto flex w-full max-w-4xl flex-col gap-8 px-6 py-12">
			<header className="flex flex-col gap-2">
				<p className="text-xs font-semibold uppercase tracking-wide text-zinc-500">
					Creatives
				</p>
				<h1 className="text-brand-gradient text-4xl font-semibold tracking-tight sm:text-5xl">
					Upload size limit
				</h1>
				<p className="max-w-2xl text-sm text-zinc-500">
					Control the per-tenant ceiling applied to creative assets
					uploaded through the campaign builder. Bounds run from 1 MB
					to 500 MB with a platform default of 50 MB. Freeze the limit
					to require platform-admin sign-off on future edits.
				</p>
			</header>

			<CreativeSizeLimitPage />

			<footer className="flex flex-col gap-1 border-t border-zinc-100 pt-6 text-xs text-zinc-400">
				<span>
					Enforcement runs in the upload worker before bytes stream to
					object storage — pre-flight checks are cheap and cannot be
					bypassed by the client.
				</span>
				<span>
					Reset to default clears the tenant override and falls back
					to the platform ceiling.
				</span>
			</footer>
		</main>
	);
}

/**
 * TanStack Router leaf definition. The scaffold owner will add this to the
 * route tree in `router.tsx` during wire-up:
 *
 *   import { creativeSizeLimitRoute } from "./routes/creatives.size-limit";
 *   rootRoute.addChildren([indexRoute, creativeSizeLimitRoute, ...]);
 */
export const creativeSizeLimitRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/creatives/size-limit",
	component: CreativeSizeLimitRoute,
});
